import { useAFormHandler } from 'analytics/hooks'
import { useFormik } from 'formik'
import { Product } from 'modules/domain/collection/types'
import { refetchFunc, useSingleEntity } from 'modules/domain/common/hooks'
import { Deal, DealContractType } from 'modules/domain/deal/types'
import { ProductPurpose } from 'modules/domain/documentRequest/types'
import { endpoints } from 'modules/endpoints'
import { apiClient } from 'modules/utils/httpClient'
import { useTranslation } from 'react-i18next'
import { ResponsiveModal } from 'views/components/ResponsiveModal/ResponsiveModal'
import { SharedModalContent } from 'views/components/SharedModal'
import { DealContractForm } from 'views/pages/Deal/DealDetail/types'
import { CommonFields } from './CommonFields'

type Props = {
  deal: Deal
  onClose: () => void
  onSuccess: refetchFunc
}

export const ContractRequestModal = ({ onClose, deal, onSuccess }: Props) => {
  const { t } = useTranslation('deal')
  const [, product] = useSingleEntity<Product>(endpoints.products(deal.product.id))
  const { formProgress, formHandler } = useAFormHandler('sendContractRequest', { id: deal.id })
  const hasGroup = deal.sale_bid.company.is_in_group

  const formik = useFormik<DealContractForm>({
    initialValues: {
      deal: deal.id,
      contract_type: DealContractType.agroclub,
      dispatching_date: '',
      payment_terms: '',
      transfer_address: '',
      email: !hasGroup ? deal.sale_bid.owner.email : '',
      phone: !hasGroup ? deal.sale_bid.owner.phone : '',
      conformity_declaration_type: DealContractType.agroclub,
      season: deal.season?.startDate ?? '',
      quantity: deal.quantity,
      price: deal.price_exw,
      has_vat: false,
      product_purpose: ProductPurpose.alimentary,
      parameters: [],
      main_param: {
        parameter: product?.main_parameter?.id,
        parameter_value: undefined,
      },
      parameters_comment: '',
    },
    enableReinitialize: true,

    onSubmit: formHandler(
      async () => {
        const dto = {
          ...formik.values,
          parameters: [formik.values.main_param, ...formik.values.parameters],
        }
        delete dto.main_param
        await apiClient.post(endpoints.dealsContracts(), dto)
      },
      {
        onSuccess: async () => {
          await onSuccess()
          onClose()
        },
        onError: (error) => {
          const { errors } = error
          formik.setErrors(errors)
        },
      },
    ),
  })

  return (
    <ResponsiveModal
      title={t('requestDocsModal.title')}
      id="requestDocs"
      onClose={onClose}
      progress={formProgress}
      onConfirm={() => formik.submitForm()}
      confirmButtonText={t('common:send')}
    >
      <SharedModalContent>
        <CommonFields deal={deal} formik={formik} product={product} />
      </SharedModalContent>
    </ResponsiveModal>
  )
}
